/**
 * User Store - Authentication and Profile Management
 *
 * Manages user login state, profile data, and permissions.
 * Provides hooks for authentication status and user actions.
 */

import type { AsyncStoreActions, BaseStore } from "../../core";
import { createAsyncActions, createStore, withLogging } from "../../core";
import type { UserProfile } from "../../types";
import { toPersistedUserProfile } from "./persistedUserProfile";

/** Loading state of the permissions granted to the role selected at `/additional-info`. */
export type RolePermissionsStatus = "idle" | "loading" | "ready" | "error";

// User store state interface
interface UserState extends BaseStore, AsyncStoreActions {
  // Data
  profile: UserProfile | null;
  /** Union of the permissions of every role the user holds (`/users/current/permissions`). */
  permissions: string[];
  /** Permissions of the single role selected at `/additional-info` (v2 catalogue). */
  rolePermissions: string[];
  /** Role id the `rolePermissions` list was loaded for. */
  rolePermissionsRoleId: string | null;
  rolePermissionsStatus: RolePermissionsStatus;

  // Computed state
  isAuthenticated: boolean;

  // Actions
  setProfile: (profile: UserProfile) => void;
  updateProfile: (updates: Partial<UserProfile>) => void;
  setPermissions: (permissions: string[]) => void;
  setRolePermissions: (
    roleId: string | null,
    permissions: string[],
    status: RolePermissionsStatus,
  ) => void;
  setRolePermissionsStatus: (status: RolePermissionsStatus) => void;
  login: (profile: UserProfile) => void;
  logout: () => void;
}

// Initial state
const initialState = {
  profile: null,
  permissions: [],
  rolePermissions: [],
  rolePermissionsRoleId: null,
  rolePermissionsStatus: "idle" as RolePermissionsStatus,
  isAuthenticated: false,
};

// Create the user store
export const useUserStore = createStore<UserState>(
  withLogging("UserStore", (set) => ({
    ...initialState,
    ...createAsyncActions<UserState>(),
    _initialized: false,
    _lastUpdated: Date.now(),

    // Basic actions
    setProfile: (profile: UserProfile) =>
      set((state) => {
        state.profile = profile;
        state.isAuthenticated = true;
        state._lastUpdated = Date.now();
      }),

    updateProfile: (updates: Partial<UserProfile>) =>
      set((state) => {
        if (state.profile) {
          // Merge updates into existing profile
          state.profile = { ...state.profile, ...updates };
          state._lastUpdated = Date.now();
        }
      }),

    setPermissions: (permissions: string[]) =>
      set((state) => {
        state.permissions = permissions;
        state._lastUpdated = Date.now();
      }),

    // One atomic write: the list, the role it belongs to and the load outcome always land
    // together, so no subscriber ever observes "ready" with a list that is not the result.
    setRolePermissions: (
      roleId: string | null,
      permissions: string[],
      status: RolePermissionsStatus,
    ) =>
      set((state) => {
        state.rolePermissions = permissions;
        state.rolePermissionsRoleId = roleId;
        state.rolePermissionsStatus = status;
        state._lastUpdated = Date.now();
      }),

    setRolePermissionsStatus: (status: RolePermissionsStatus) =>
      set((state) => {
        state.rolePermissionsStatus = status;
        state._lastUpdated = Date.now();
      }),

    login: (profile: UserProfile) =>
      set((state) => {
        state.profile = profile;
        state.isAuthenticated = true;
        state.error = null;
        state._lastUpdated = Date.now();
      }),

    logout: () =>
      set((state) => {
        state.profile = null;
        state.isAuthenticated = false;
        state.permissions = [];
        state.rolePermissions = [];
        state.rolePermissionsRoleId = null;
        state.rolePermissionsStatus = "idle";
        state.error = null;
        state._lastUpdated = Date.now();
      }),
  })),
  {
    name: "user-store",
    persist: true,
    version: 1,
    partialize: (state) => ({
      profile: toPersistedUserProfile(state.profile),
      isAuthenticated: state.isAuthenticated,
      permissions: state.permissions,
      // The selected role's permissions are deliberately NOT persisted: a stale "ready" would
      // short-circuit the reload on the next bootstrap and pin the session to old grants.
      // They are an in-memory cache for the life of the tab, refetched on every init.
    }),
  },
);

// ===== SIMPLIFIED DOMAIN HOOK =====
/**
 * Get user store state (simplified access)
 * @returns Complete user state
 */
export const useUser = () => useUserStore((state) => state);

// ===== DATA SELECTORS =====
/**
 * Get current user permissions
 * @returns Array of permission strings
 */
export const useUserPermissions = () => useUserStore((state) => state.permissions);

/**
 * Get the permissions granted to the role selected at `/additional-info`
 * @returns Array of v2 permission names (empty until loaded, which denies everything)
 */
export const useRolePermissions = () => useUserStore((state) => state.rolePermissions);

/**
 * Get the load state of the selected role's permissions
 */
export const useRolePermissionsStatus = () => useUserStore((state) => state.rolePermissionsStatus);

/**
 * Get the selected role's permissions (utility function for non-hook contexts)
 */
export const getRolePermissions = (): string[] => useUserStore.getState().rolePermissions;

/**
 * Get current user profile
 * @returns User profile or null if not authenticated
 */
export const useUserProfile = () => useUserStore((state) => state.profile);

/**
 * Get current user profile (utility function for non-hook contexts)
 * @returns User profile or null if not authenticated
 */
export const getCurrentProfile = () => useUserStore.getState().profile;
